import { NavigationInterruptError } from "@/domain/entities/Error";
import { ApiPublicPaymentService } from "@/infrastructure/services/ApiPublicPaymentService";
import { AdyenPaymentService } from "@/infrastructure/services/AdyenPaymentService";
import { logger } from "@/infrastructure/services/LoggerService";
import { ErrorHandler, ERROR_CONTEXTS } from "@/shared/utils/errorHandler";
import { isApiErrorCode } from "@/shared/utils/errorUtils";
import { PaymentLogUseCases } from "./PaymentLogUseCases";
export class PublicPaymentUseCases {
    constructor(publicPaymentService, adyenService, paymentLogUseCases) {
        Object.defineProperty(this, "publicPaymentService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "adyenService", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "paymentLogUseCases", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.publicPaymentService = publicPaymentService || new ApiPublicPaymentService();
        this.adyenService = adyenService || new AdyenPaymentService();
        this.paymentLogUseCases = paymentLogUseCases || new PaymentLogUseCases();
    }
    /**
     * Initialize payment page data
     * Fetches organization info, payment details, and payment token in parallel
     */
    async initializePaymentPage(paymentId) {
        let organizationInfo;
        let paymentDetails;
        try {
            logger.debug(`Initializing payment page for payment ID: ${paymentId}`);
            // Fetch organization and payment details first to get urlCancelReturn
            [organizationInfo, paymentDetails] = await Promise.all([
                this.publicPaymentService.getOrganizationInfo(paymentId),
                this.publicPaymentService.getPaymentDetails(paymentId),
            ]);
            // Then fetch payment session (most likely to fail for canceled payments)
            const paymentSession = await this.publicPaymentService.getPaymentSession(paymentId);
            logger.debug("Payment page data loaded successfully");
            return {
                organizationInfo,
                paymentDetails,
                paymentSession,
            };
        }
        catch (error) {
            // Check if this is a PaymentSessionProcessingCanceledError
            if (isApiErrorCode(error, "PaymentSessionProcessingCanceledError")) {
                // Handle payment canceled error with specific UI and navigate to error page
                const urlCancelReturn = paymentDetails?.urlCancelReturn || null;
                ErrorHandler.handlePaymentCanceled(urlCancelReturn);
                // Throw NavigationInterruptError to signal that navigation is happening
                // The calling component should catch this and return early
                throw new NavigationInterruptError("Payment canceled - navigating to error page");
            }
            // Use the centralized error handler with automatic navigation logic
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.PAYMENT_INITIALIZATION);
            throw error;
        }
    }
    /**
     * Initialize payment success page data
     * Fetches only organization info and payment details (no payment session needed)
     */
    async initializePaymentSuccessPage(paymentId) {
        try {
            logger.debug(`Initializing payment success page for payment ID: ${paymentId}`);
            // Fetch only the data needed for success page - no payment session required
            const [organizationInfo, paymentDetails] = await Promise.all([
                this.publicPaymentService.getOrganizationInfo(paymentId),
                this.publicPaymentService.getPaymentDetails(paymentId),
            ]);
            logger.debug("Payment success page data loaded successfully");
            return {
                organizationInfo,
                paymentDetails,
            };
        }
        catch (error) {
            // Use the centralized error handler with automatic navigation logic
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.PAYMENT_INITIALIZATION);
            throw error;
        }
    }
    /**
     * Initialize Adyen payment system
     */
    async initializeAdyen(config) {
        try {
            logger.debug("Initializing Adyen payment system");
            await this.adyenService.initialize(config);
            logger.debug("Adyen initialization completed");
        }
        catch (error) {
            // Use the Adyen error handler for simplified error messages
            ErrorHandler.handleAdyenError(error, ERROR_CONTEXTS.ADYEN_INITIALIZATION);
            throw error;
        }
    }
    /**
     * Check if Adyen is ready for payment processing
     */
    isAdyenReady() {
        return this.adyenService.isReady();
    }
    /**
     * Get Adyen Drop-in component for rendering (legacy method)
     */
    getAdyenDropInComponent() {
        try {
            return this.adyenService.getDropInComponent();
        }
        catch (error) {
            // Use the centralized error handler with automatic navigation logic
            ErrorHandler.handleAdyenError(error, ERROR_CONTEXTS.ADYEN_DROP_IN_RETRIEVAL);
            throw error;
        }
    }
    /**
     * Check if Adyen Drop-in component is configured and safe to unmount
     */
    isAdyenDropinReady() {
        return this.adyenService.isDropinReady();
    }
    /**
     * Process payment with Adyen
     */
    async processPayment(paymentData) {
        try {
            logger.debug("Processing payment...");
            const result = await this.adyenService.processPayment(paymentData);
            logger.debug("Payment processed successfully");
            return result;
        }
        catch (error) {
            // Use the centralized error handler - payment processing errors should not navigate
            ErrorHandler.handleApiError(error, ERROR_CONTEXTS.PAYMENT_PROCESSING, false);
            throw error;
        }
    }
    /**
     * Get payment logging use cases for manual logging
     */
    getPaymentLogUseCases() {
        return this.paymentLogUseCases;
    }
    /**
     * Cancel a payment
     * @param paymentId - The payment ID to cancel
     */
    async cancelPayment(paymentId) {
        logger.debug(`Canceling payment with ID: ${paymentId}`);
        // CONSTRAINT: no ErrorHandler call here. It would overwrite errorStore.currentError, and the
        // refused-payment page (Error.vue) renders that state: a failed cancel would wipe its buttons.
        // Callers report the rejection themselves.
        await this.publicPaymentService.cancelPayment(paymentId);
        logger.debug("Payment cancelled successfully");
    }
    /**
     * Record the payer's final PAD agreement type at mandate confirmation.
     * @param paymentId - The payment ID
     * @param padType - The chosen PAD type (Personal/Business)
     */
    async updatePadType(paymentId, padType) {
        // Deliberately no ErrorHandler.handleApiError here: recording the PAD type is a
        // non-blocking step (the payment proceeds regardless), so a failure must NOT populate
        // the global error store and surface a spurious toast after a successful payment. The
        // error propagates to the caller (PublicPayment.onPadConfirm), which logs it and continues.
        logger.debug(`Recording PAD type ${padType} for payment ID: ${paymentId}`);
        await this.publicPaymentService.updatePadType(paymentId, padType);
    }
}
